import React from 'react';
import { Paper, Box, Typography, Stack, Chip, alpha } from '@mui/material';
import { HourglassEmpty, Person, ConfirmationNumber } from '@mui/icons-material';
import { ILocationList } from '../../../interfaces/ILocationList';
import { IPublicEventDetails } from '../../../interfaces/IPublicEventDetails';

interface PendingApprovalSummaryProps {
    event: IPublicEventDetails;
    locations: ILocationList[];
    hasTickets?: boolean; // Indicates if user has tickets or is on waitlist
}

const PendingApprovalSummary: React.FC<PendingApprovalSummaryProps> = ({
    event,
    locations,
    hasTickets = false,
}) => {
    const isWaitlist = !hasTickets;
    
    return (
        <Paper 
            elevation={3} 
            sx={{ 
                p: 4, 
                borderRadius: 3, 
                mb: 4,
                border: `1px solid ${alpha('#493193', 0.1)}`,
                boxShadow: `0 8px 32px ${alpha('#493193', 0.08)}`,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 700,
                            background: `linear-gradient(135deg, #493193 0%, #2fadd8 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        {isWaitlist ? 'Expression of Interest Pending Approval' : 'Registration Pending Approval'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                            icon={<HourglassEmpty />}
                            label="Awaiting Review"
                            sx={{
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                height: { xs: 28, sm: 32 },
                                fontWeight: 600,
                                borderRadius: '50px',
                                bgcolor: alpha('#ed6c02', 0.1),
                                color: '#ed6c02',
                                border: `1px solid ${alpha('#ed6c02', 0.2)}`,
                                '& .MuiChip-icon': {
                                    fontSize: { xs: 16, sm: 22 },
                                    color: '#ed6c02',
                                },
                            }}
                        />
                        <Chip
                            icon={isWaitlist ? <Person /> : <ConfirmationNumber />}
                            label={isWaitlist ? 'Waitlist' : 'Registration Submitted'}
                            sx={{
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                height: { xs: 28, sm: 32 },
                                fontWeight: 600,
                                borderRadius: '50px',
                                bgcolor: alpha('#0288d1', 0.1),
                                color: '#0288d1',
                                border: `1px solid ${alpha('#0288d1', 0.2)}`,
                                '& .MuiChip-icon': {
                                    fontSize: { xs: 16, sm: 22 },
                                    color: '#0288d1',
                                },
                            }}
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {isWaitlist 
                            ? 'Your expression of interest in this event is awaiting organiser approval. You\'ll be notified when your request has been reviewed.'
                            : 'Your registration for this event is awaiting organiser approval. You\'ll receive an email notification once your request has been reviewed.'
                        }
                    </Typography>
                </Box>
            </Box>

            {/* Info Box */}
            <Box 
                sx={{ 
                    mt: 3, 
                    p: 3, 
                    bgcolor: alpha('#493193', 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha('#493193', 0.15)}`,
                }}
            >
                <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: '#493193' }}>
                    What happens next?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {isWaitlist
                        ? 'The event organiser will review your expression of interest. Once approved, you\'ll be added to the waitlist. If tickets become available, you\'ll receive priority notification and can then register for the event.'
                        : 'The event organiser will review your registration. Once approved, you\'ll receive confirmation via email and will be able to access your ticket.'
                    }
                </Typography>
            </Box>
        </Paper>
    );
};

export default PendingApprovalSummary;
